import * as Api from '@ViewModels';
import * as React from 'react';
import { PersistentStorageManager } from '../../../../../models/Storage';
import { CONTACT_SORT_PREFERENCE_SUFFIX, DefaultPeopleSort } from '../PeopleContext';
import { PeopleSearchStorageKey } from './useContactsPageAppDrawerQueryParamState';

interface UseContactSortPreferencesParams {
	userSession: {
		user?: {
			id?: string;
		};
	};
	currentSort: Api.ISortDescriptor;
	contactsQueryParams?: any;
	storedContactsQuery?: {
		searchRequest: Api.IBulkContactsRequest;
		sortDescriptor: Api.ISortDescriptor;
	} | null;
}

interface UseContactSortPreferencesReturn {
	isNameSort: (sortDescriptor: Api.ISortDescriptor) => boolean;
	loadUserSortPreference: () => Api.ISortDescriptor | null;
	saveUserSortPreference: (sortPreference: Api.ISortDescriptor) => void;
	resolveSortPriority: (
		queryParams: any,
		storedQuery: any,
		userPreference: Api.ISortDescriptor | null
	) => Api.ISortDescriptor;
	resolvedSort: Api.ISortDescriptor;
}

export const useContactSortPreferences = ({
	userSession,
	currentSort,
	contactsQueryParams,
	storedContactsQuery,
}: UseContactSortPreferencesParams): UseContactSortPreferencesReturn => {
	// Helper function to generate user-specific storage key
	const getUserSortPreferenceKey = React.useCallback(
		() => `${userSession.user?.id}${CONTACT_SORT_PREFERENCE_SUFFIX}`,
		[userSession.user?.id]
	);

	// Function to determine if a sort is name-based
	const isNameSort = React.useCallback((sortDescriptor: Api.ISortDescriptor) => {
		return ['firstName', 'lastName', 'handle'].includes(sortDescriptor.sortBy || '');
	}, []);

	// Function to load user sort preference from localStorage
	const loadUserSortPreference = React.useCallback((): Api.ISortDescriptor | null => {
		if (!userSession.user?.id) return null;
		try {
			const storedValue = PersistentStorageManager.local.get(getUserSortPreferenceKey());
			return storedValue ? JSON.parse(storedValue) : null;
		} catch {
			// Clear corrupted preference
			PersistentStorageManager.local.remove(getUserSortPreferenceKey());
			return null;
		}
	}, [userSession.user?.id, getUserSortPreferenceKey]);

	// Function to save user sort preference to localStorage
	const saveUserSortPreference = React.useCallback(
		(sortPreference: Api.ISortDescriptor) => {
			if (userSession.user?.id && isNameSort(sortPreference)) {
				PersistentStorageManager.local.set(getUserSortPreferenceKey(), JSON.stringify(sortPreference));
			}
		},
		[userSession.user?.id, getUserSortPreferenceKey, isNameSort]
	);

	// Function to resolve sort priority
	const resolveSortPriority = React.useCallback(
		(queryParams: any, storedQuery: any, userPreference: Api.ISortDescriptor | null): Api.ISortDescriptor => {
			return queryParams?.sortDescriptor || storedQuery?.sortDescriptor || userPreference || DefaultPeopleSort;
		},
		[]
	);

	// Compute the resolved initial sort
	const userSortPreference = loadUserSortPreference();
	const resolvedSort = resolveSortPriority(contactsQueryParams, storedContactsQuery, userSortPreference);

	// Save user sort preference when current sort changes
	React.useEffect(() => {
		saveUserSortPreference(currentSort);
	}, [currentSort, saveUserSortPreference]);

	// Update stored search sort when sort changes to prevent location effect from overriding
	React.useEffect(() => {
		const storedValue = PersistentStorageManager.local.get(PeopleSearchStorageKey);
		if (storedValue) {
			try {
				const storedData = JSON.parse(storedValue);
				if (storedData && storedData.sortDescriptor) {
					storedData.sortDescriptor = currentSort;
					PersistentStorageManager.local.set(PeopleSearchStorageKey, JSON.stringify(storedData));
				}
			} catch {
				// If stored data is corrupted, ignore the update
				// The next load will reset with clean data
			}
		}
	}, [currentSort]);

	return {
		isNameSort,
		loadUserSortPreference,
		saveUserSortPreference,
		resolveSortPriority,
		resolvedSort,
	};
};
